import React, { useState, useEffect } from "react";
import NavBar from "./components/NavBar"; // New NavBar component
import About from "./components/About";
import TechStack from "./components/TechStack";
import Projects from "./components/Projects";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ContactSection from "./components/Contact";
import TestimonialsSection from "./components/TestimonialsSection";
import PageLoader from "./components/loaders/PageLoader";
import {
  getProfilePhoto,
  getProfile,
  getAbout,
  getExperiences,
  getEducations,
  getTechStacks,
  getLatestProjects,
  getReviews,
} from "./services/apiServices";
import profileImage from "./assets/Profile/profileImage.jpg";

function App() {
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference first
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // If no saved preference, use system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return true;
    }
    return false;
  });
  const [profileData, setProfileData] = useState({
    profilePhoto: profileImage,
    name: "Abdul Wadood",
    description: "I'm a software developer with a passion for building innovative solutions.",
  });
  const [aboutData, setAboutData] = useState({
    description: "The Generator App is an online tool that helps you export ready-made templates for your future website. It allows you to combine slides, panels, and other components, exporting them as static HTML/CSS/JS files.",
    experiences: [],
    educations: [],
  });
  const [techStacks, setTechStacks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);

  // Toggle dark mode and save to localStorage
  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem("darkMode", JSON.stringify(newMode));
      return newMode;
    });
  };

  // Apply dark mode class to root element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // Update document title and meta description dynamically
  useEffect(() => {
    if (!loading && profileData.name) {
      document.title = `${profileData.name} - Software Developer | Portfolio`;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          `${profileData.description || "Software Developer"} - View my portfolio, projects, and experience.`
        );
      }
    }
  }, [loading, profileData.name, profileData.description]);

  // Listen for system preference changes (only if no manual preference is saved)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only update if user hasn't manually set a preference
      const saved = localStorage.getItem("darkMode");
      if (saved === null) {
        setDarkMode(e.matches);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Add structured data for SEO
  useEffect(() => {
    const updateStructuredData = () => {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": profileData.name,
        "jobTitle": "Software Developer",
        "description": profileData.description,
        "url": window.location.origin,
        "image": profileData.profilePhoto,
        "sameAs": [
          "https://github.com/AbdulWadood2",
          "https://www.linkedin.com/in/abdul-wadood-ba9414246/",
          "https://x.com/AbdulWa34577265"
        ],
        "knowsAbout": techStacks.map(tech => tech.name),
        "alumniOf": aboutData.educations.map(edu => ({
          "@type": "EducationalOrganization",
          "name": edu.institution
        })),
        "worksFor": aboutData.experiences.map(exp => ({
          "@type": "Organization",
          "name": exp.company
        }))
      };

      // Remove existing structured data script if any
      const existingScript = document.getElementById("structured-data");
      if (existingScript) {
        existingScript.remove();
      }

      // Add new structured data
      const script = document.createElement("script");
      script.id = "structured-data";
      script.type = "application/ld+json";
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    };

    if (!loading) {
      updateStructuredData();
    }
  }, [loading, profileData, techStacks, aboutData]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // Fetch all data in parallel
        const [
          photoResponse,
          profileResponse,
          aboutResponse,
          experiencesResponse,
          educationsResponse,
          techStacksResponse,
          projectsResponse,
          reviewsResponse,
        ] = await Promise.all([
          getProfilePhoto().catch(() => null),
          getProfile().catch(() => null),
          getAbout().catch(() => null),
          getExperiences().catch(() => null),
          getEducations().catch(() => null),
          getTechStacks().catch(() => null),
          getLatestProjects().catch(() => null),
          getReviews().catch(() => null),
        ]);

        // Set profile data
        if (photoResponse && photoResponse.data && photoResponse.data.profilePhoto) {
          setProfileData((prev) => ({
            ...prev,
            profilePhoto: photoResponse.data.profilePhoto,
          }));
        }

        if (profileResponse && profileResponse.data) {
          setProfileData((prev) => ({
            ...prev,
            name: profileResponse.data.name || prev.name,
            description: profileResponse.data.description || prev.description,
          }));
        }

        // Set about data
        if (aboutResponse && aboutResponse.data && aboutResponse.data.description) {
          setAboutData((prev) => ({
            ...prev,
            description: aboutResponse.data.description,
          }));
        }

        if (experiencesResponse && experiencesResponse.data) {
          setAboutData((prev) => ({
            ...prev,
            experiences: experiencesResponse.data,
          }));
        }

        if (educationsResponse && educationsResponse.data) {
          setAboutData((prev) => ({
            ...prev,
            educations: educationsResponse.data,
          }));
        }

        // Set tech stacks
        if (techStacksResponse && techStacksResponse.data) {
          setTechStacks(techStacksResponse.data);
        }

        // Set projects
        if (projectsResponse && projectsResponse.status === "success") {
          setProjects(projectsResponse.data || []);
        }

        // Set reviews
        if (reviewsResponse) {
          if (reviewsResponse.status === "success" && reviewsResponse.data) {
            setReviews(reviewsResponse.data);
          } else if (reviewsResponse.data) {
            // Handle case where data might be directly in response
            setReviews(Array.isArray(reviewsResponse.data) ? reviewsResponse.data : []);
          }
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <main className="bg-white dark:bg-gray-900">
      {/* Navigation Bar */}
      <NavBar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="px-6 md:px-16 pt-16">
        {/* Home Section */}
        <section id="home">
          <Header
            profilePhoto={profileData.profilePhoto}
            name={profileData.name}
            description={profileData.description}
          />
        </section>

        {/* About Section */}
        <section id="about">
          <About
            aboutDescription={aboutData.description}
            experiences={aboutData.experiences}
            education={aboutData.educations}
          />
        </section>

        {/* Tech Stack Section */}
        <section id="techstack">
          <TechStack techStacks={techStacks} />
        </section>
        {/* Projects Section */}
        <section id="projects">
          <Projects projects={projects} />
        </section>
      </div>
      {/* Testimonials Section */}
      <section id="testimonials">
        <TestimonialsSection reviews={reviews} />
      </section>
      <section id="contact">
        <ContactSection />
      </section>
      {/* Contact Section */}
      <section>
        <Footer />
      </section>
    </main>
  );
}

export default App;
